import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import ApiService from "../../services/api-service";
import NoData from "../../components/Nodata";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";
import "../../pages/Home/Home.scss";

export default function SimpleVideoCarousel() {
  const { t } = useTranslation("common");
  const history = useHistory();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await ApiService.FEATURE_VIDEO('pageSize=6&pageNo=1');
      if (response && response.result && response.result.length > 0) {
        setVideos(response.result);
      } else {
        setVideos([]);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching videos:", error);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleVideoClick = (video) => {
    try {
      if (!video._id) {
        return;
      }

      let playlistId = null;

      if (video.video?.url) {
        const videoUrlMatch = video.video.url.match(/\/([a-f0-9]{24})\//);
        if (videoUrlMatch) {
          playlistId = videoUrlMatch[1];
        }
      }

      if (!playlistId && video.thumbnail?.url) {
        const thumbnailUrlMatch = video.thumbnail.url.match(/\/([a-f0-9]{24})\//);
        if (thumbnailUrlMatch) {
          playlistId = thumbnailUrlMatch[1];
        }
      }

      if (!playlistId && video.studioId && typeof video.studioId === 'string') {
        playlistId = video.studioId;
      }

      if (playlistId && video._id) {
        history.push(`/app/video-player/${playlistId}/${video._id}`);
      } else {
        let uploaderStudioId = video.uploader?._id || video.uploader;
        if (uploaderStudioId) {
          history.push(`/app/video-player/${uploaderStudioId}/${video._id}`);
        }
      }
    } catch (error) {
      let uploaderStudioId = video.uploader?._id || video.uploader;
      if (uploaderStudioId) {
        history.push(`/app/video-player/${uploaderStudioId}/${video._id}`);
      }
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const formatViews = (views) => {
    if (!views) return "0 views";
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M views`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K views`;
    return `${views} views`;
  };

  if (loading) {
    return <Loader />;
  }

  if (videos.length === 0) {
    return null;
  }

  return (
    <section className="siki-videos-section siki-fade-up siki-delay-2">
      <div className="siki-section-connector"></div>
      <div className="siki-section-label">Watch</div>
      <div className="siki-section-header">
        <h2 className="siki-section-title">Featured Videos</h2>
        <div className="siki-section-action" onClick={() => history.push("/app/feature/videos")}>
          See All →
        </div>
      </div>

      <div className="siki-videos-grid">
        {videos.slice(0, 6).map((video) => (
          <div
            key={video._id}
            className="siki-video-card"
            onClick={() => handleVideoClick(video)}
          >
            <img
              className="siki-video-thumb"
              src={video.thumbnail?.url || video.thumbnailUrl || video.thumbnail || "https://via.placeholder.com/640x360"}
              alt={video.title || video.name || 'Video'}
              onError={(e) => {
                e.target.src = "https://via.placeholder.com/640x360?text=No+Thumbnail";
              }}
            />
            {formatDuration(video.duration) && (
              <div className="siki-video-duration">{formatDuration(video.duration)}</div>
            )}
            <div className="siki-video-overlay">
              <div className="siki-video-title">{video.title || video.name || 'Untitled Video'}</div>
              <div className="siki-video-meta">
                <span>{video.playlistName || video.studioName || (video.uploader?.firstName ? `${video.uploader.firstName} ${video.uploader.lastName || ''}`.trim() : 'Unknown Artist')}</span>
                <span>{formatViews(video.views || video.listens || 0)}</span>
              </div>
            </div>
            <div className="siki-video-play">
              <svg viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21"/></svg>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
